"use client";

// The theme, and the standard way of holding one in a Next app.
//
// next-themes rather than a hand-rolled cookie, and the difference is the flash.
// A theme read in React — from state, from a context, from localStorage in an
// effect — is necessarily read AFTER the first paint, so the page renders in the
// wrong theme and corrects itself a frame later. next-themes solves it the only
// way it can be solved: a tiny blocking <script> in <head> that reads the stored
// preference and sets the class on <html> before the browser paints anything.
// That is also why <html> carries suppressHydrationWarning in app/layout.tsx —
// the script legitimately changes an attribute the server did not write, and
// React would otherwise report it as a mismatch.
//
// The persona cookie (lib/persona-cookie.ts) is deliberately NOT the model to
// copy here, despite being the app's other persisted view preference. That one
// is a cookie because the SERVER has to know it: the persona decides what data
// the first byte of HTML contains. The theme decides nothing the server renders
// — only which colours the client paints — so it stays client-side, and stays
// out of every request header.
//
// attribute="class" puts `class="dark"` / `class="light"` on <html>, which is
// what `darkMode: "class"` in tailwind.config.ts reads and what scopes the two
// blocks of variables in globals.css.

import { ThemeProvider as NextThemeProvider } from "next-themes";

// ⚠️ THE SCRIPT IS A SCRIPT ON THE SERVER ONLY. On the server this is undefined
// and next-themes renders its plain blocking <script>, which is the whole point
// of the library. In the browser it types the element `text/plain`.
//
// Why: sometimes the client MOUNTS this provider rather than hydrating it.
// Sign-out does (a mount logger saw the provider unmount and mount again, and
// the <script> arrive with next-themes' client-only `nonce=""`). React 19 then
// creates a <script> element on the client and warns in dev: "Encountered a
// script tag while rendering React component". That lit Next's red issue pill
// over the Feedback button. A script React inserts never runs anyway, and the
// provider's own effect sets the theme class on a client mount, so marking it
// as data changes nothing but the warning (React skips data blocks:
// isScriptDataBlock). Hydration keeps the server's element as it is:
// next-themes puts suppressHydrationWarning on it, so the differing `type` is
// not a mismatch.
//
// next-themes 0.4.6 is the newest release (1.0.0-beta.0 is older) and has no
// fix of its own. A constant, not an inline object, because the script
// component is memoised on its props.
const SCRIPT_PROPS = typeof window === "undefined" ? undefined : { type: "text/plain" };

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <NextThemeProvider
      attribute="class"
      // THE APP WAS DESIGNED DARK, so dark is what an account with no stored
      // preference gets — including every existing user, whose first load after
      // this ships must look exactly like their last one before it.
      defaultTheme="dark"
      // No system option. The control is a two-position switch, so offering a
      // third state ("system") that the switch cannot show would leave the UI
      // unable to represent its own value.
      enableSystem={false}
      // Suppresses transitions for the duration of the swap — see the
      // [data-theme-changing] rule in globals.css. Without it a page full of
      // `transition-colors` cross-fades a thousand elements out of step and the
      // switch feels like a slow dissolve rather than a switch.
      // OFF, and this is not cosmetic. next-themes defaults it ON, which stamps
      // `color-scheme` onto <html> — and Chrome renders every NATIVE control from
      // that: checkboxes, selects, scrollbars. Dark mode's unchecked checkboxes
      // silently went from white to dark olive on the Users page, which is a
      // change to a palette that was hand-picked and correct. Light mode needs
      // nothing here: absent the property, controls render light already.
      enableColorScheme={false}
      disableTransitionOnChange
      scriptProps={SCRIPT_PROPS}
    >
      {children}
    </NextThemeProvider>
  );
}
